import * as PIXI from "pixi.js";
import {config} from "../config";
import {CustomSpineAnimation} from "./CustomSpineAnimation";
import {EuropeanRoulette} from "./EuropeanRoulette";
import {Utils} from "./Utils";

export class WinBanner extends CustomSpineAnimation {

    private static readonly WIN_BANNER_ANIMATION_CONFIG = {
        NAME: "winBannerAnimation",
        STATES: {
            IDLE: "idle",
            FADE_IN_RED: "fadeInRed",
            FADE_IN_BLACK: "fadeInBlack",
            FADE_IN_ZERO: "fadeInZero",
            FADE_OUT_RED: "fadeOutRed",
            FADE_OUT_BLACK: "fadeOutBlack",
            FADE_OUT_ZERO: "fadeOutZero",
        },
        SLOT_CONTAINERS: {
            CONTENT_CONTAINER: "contentContainer",
        },
    };

    private readonly HIGH_LABEL = "HIGH";
    private readonly LOW_LABEL = "LOW";
    private readonly EVEN_LABEL = "EVEN";
    private readonly ODD_LABEL = "ODD";
    private readonly YOU_WIN_LABEL = "YOU WIN";
    private readonly NO_WIN_LABEL = "NO WIN";
    private readonly STEEM_LABEL = config.displayCurrency;

    private readonly HIGH_LOW_LABEL_POSITION = new PIXI.Point(-101, 127);
    private readonly EVEN_LABEL_POSITION = new PIXI.Point(101, 127);
    private readonly DRAWN_NUMBER_POSITION = new PIXI.Point(0, 50);
    private readonly PRIZE_POSITION = new PIXI.Point(0, -73);
    private readonly WIN_LABEL_POSITION = new PIXI.Point(0, -27);
    private readonly CURRENCY_LABEL_POSITION = new PIXI.Point(0, -113);

    private readonly DEFAULT_ANCHOR_POINT: any = new PIXI.Point(0.5, 0.5);
    private readonly LABELS_STYLE = {
        fontSize: 28,
        fontFamily: "RobotoRegular",
        fill: "#fff",
    };

    private readonly DRAWN_NUMBER_STYLE = {
        fontSize: 100,
        fontFamily: "BernardMtCondensed",
        fill: "#fff",
    };

    private readonly PRIZE_STYLE = {
        fontSize: 52,
        fontFamily: "BernardMtCondensed",
        fill: "#fff",
    };

    private highLowText: PIXI.Text;
    private evenOddText: PIXI.Text;
    private drawnNumberText: PIXI.Text;
    private winInfoText: PIXI.Text;
    private prizeText: PIXI.Text;
    private currencyText: PIXI.Text;
    private numberColor: NumberColor = NumberColor.BLACK;

    constructor() {
        super(WinBanner.WIN_BANNER_ANIMATION_CONFIG.NAME);
        this.play(WinBanner.WIN_BANNER_ANIMATION_CONFIG.STATES.IDLE);
        this.getSlotContainer(WinBanner.WIN_BANNER_ANIMATION_CONFIG.SLOT_CONTAINERS.CONTENT_CONTAINER, true);
        this.createAndAddHighLowText();
        this.createAndAddEvenOddText();
        this.createAndAddDrawnNumberText();
        this.createAndAddWinText();
        this.createAndAddPrizeText();
        this.createAndAddCurrencyText();
    }

    public show(drawnNumber: number, prize: number = 0): void {
        this.drawnNumberText.text = "" + drawnNumber;

        if (prize === 0) {
            this.winInfoText.text = this.NO_WIN_LABEL;
            this.prizeText.text = "";
            this.currencyText.text = "";
        } else {
            this.winInfoText.text = this.YOU_WIN_LABEL;
            this.prizeText.text = Utils.formatAmount(prize);
            this.currencyText.text = this.STEEM_LABEL;
        }

        if (drawnNumber === 0) {
            this.highLowText.text = "";
            this.evenOddText.text = "";
            this.numberColor = NumberColor.GREEN;
            this.play(WinBanner.WIN_BANNER_ANIMATION_CONFIG.STATES.FADE_IN_ZERO);
            return;
        }

        if (drawnNumber > 18) {
            this.highLowText.text = this.HIGH_LABEL;
        } else {
            this.highLowText.text = this.LOW_LABEL;
        }

        if (drawnNumber % 2 === 0) {
            this.evenOddText.text = this.EVEN_LABEL;
        } else {
            this.evenOddText.text = this.ODD_LABEL;
        }

        if (EuropeanRoulette.RED_COLOR_NUMBERS.indexOf(drawnNumber) > -1) {
            this.numberColor = NumberColor.RED;
            this.play(WinBanner.WIN_BANNER_ANIMATION_CONFIG.STATES.FADE_IN_RED);
        } else {
            this.numberColor = NumberColor.BLACK;
            this.play(WinBanner.WIN_BANNER_ANIMATION_CONFIG.STATES.FADE_IN_BLACK);
        }
    }

    public hide(): void {
        if (this.numberColor === NumberColor.BLACK) {
            this.play(WinBanner.WIN_BANNER_ANIMATION_CONFIG.STATES.FADE_OUT_BLACK);
        } else if (this.numberColor === NumberColor.RED) {
            this.play(WinBanner.WIN_BANNER_ANIMATION_CONFIG.STATES.FADE_OUT_RED);
        } else {
            this.play(WinBanner.WIN_BANNER_ANIMATION_CONFIG.STATES.FADE_OUT_ZERO);
        }
    }

    private createAndAddHighLowText(): void {
        this.highLowText = new PIXI.Text("", this.LABELS_STYLE);
        this.highLowText.position = this.HIGH_LOW_LABEL_POSITION;
        this.highLowText.anchor.x = 0;
        this.highLowText.anchor.y = 0.5;
        this.highLowText.scale.y = -1;
        this.getSlotContainer(WinBanner.WIN_BANNER_ANIMATION_CONFIG.SLOT_CONTAINERS.CONTENT_CONTAINER)
            .addChild(this.highLowText);
    }

    private createAndAddEvenOddText(): void {
        this.evenOddText = new PIXI.Text("", this.LABELS_STYLE);
        this.evenOddText.position = this.EVEN_LABEL_POSITION;
        this.evenOddText.anchor.x = 1;
        this.evenOddText.anchor.y = 0.5;
        this.evenOddText.scale.y = -1;
        this.getSlotContainer(WinBanner.WIN_BANNER_ANIMATION_CONFIG.SLOT_CONTAINERS.CONTENT_CONTAINER)
            .addChild(this.evenOddText);
    }

    private createAndAddDrawnNumberText(): void {
        this.drawnNumberText = new PIXI.Text("", this.DRAWN_NUMBER_STYLE);
        this.drawnNumberText.position = this.DRAWN_NUMBER_POSITION;
        this.drawnNumberText.anchor = this.DEFAULT_ANCHOR_POINT;
        this.drawnNumberText.scale.y = -1;
        this.getSlotContainer(WinBanner.WIN_BANNER_ANIMATION_CONFIG.SLOT_CONTAINERS.CONTENT_CONTAINER)
            .addChild(this.drawnNumberText);

    }

    private createAndAddWinText(): void {
        this.winInfoText = new PIXI.Text("", this.LABELS_STYLE);
        this.winInfoText.position = this.WIN_LABEL_POSITION;
        this.winInfoText.anchor = this.DEFAULT_ANCHOR_POINT;
        this.winInfoText.scale.y = -1;
        this.getSlotContainer(WinBanner.WIN_BANNER_ANIMATION_CONFIG.SLOT_CONTAINERS.CONTENT_CONTAINER)
            .addChild(this.winInfoText);

    }

    private createAndAddPrizeText(): void {
        this.prizeText = new PIXI.Text("", this.PRIZE_STYLE);
        this.prizeText.position = this.PRIZE_POSITION;
        this.prizeText.anchor = this.DEFAULT_ANCHOR_POINT;
        this.prizeText.scale.y = -1;
        this.getSlotContainer(WinBanner.WIN_BANNER_ANIMATION_CONFIG.SLOT_CONTAINERS.CONTENT_CONTAINER)
            .addChild(this.prizeText);

    }

    private createAndAddCurrencyText(): void {
        this.currencyText = new PIXI.Text("", this.LABELS_STYLE);
        this.currencyText.position = this.CURRENCY_LABEL_POSITION;
        this.currencyText.anchor = this.DEFAULT_ANCHOR_POINT;
        this.currencyText.scale.y = -1;
        this.getSlotContainer(WinBanner.WIN_BANNER_ANIMATION_CONFIG.SLOT_CONTAINERS.CONTENT_CONTAINER)
            .addChild(this.currencyText);
    }
}

enum NumberColor {
    RED = 0,
    BLACK = 1,
    GREEN = 2,
}



// WEBPACK FOOTER //
// ./src/app/WinBanner.ts